// AllPayments.js
import React, { useEffect, useState } from "react";
import axios from "axios";

const AllPayments = () => {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of items per page
  const token = localStorage.getItem("token"); // Pobierz token z localStorage

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/user/getUserById/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setUsers((prev) => ({
        ...prev,
        [userId]: response.data,
      }));
    } catch (err) {
      console.error(`Failed to fetch user details: ${err.message}`);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payment/getPayments`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Sort payments by date (newest first)
      const sortedPayments = response.data.sort(
        (a, b) => new Date(b.data_platnosci) - new Date(a.data_platnosci)
      );
      setPayments(sortedPayments);

      const uniqueUserIds = [
        ...new Set(response.data.map((payment) => payment.uzytkownik_id?._id)),
      ];
      uniqueUserIds.forEach((userId) => {
        if (userId) fetchUserDetails(userId);
      });

      setLoading(false);
    } catch (err) {
      setError(
        `Failed to fetch payments: ${
          err.response?.data?.message || err.message
        }`
      );
      setLoading(false);
    }
  };

  const deletePayment = async (paymentId, karnetId) => {
    if (window.confirm("Czy na pewno chcesz usunąć tę płatność?")) {
      try {
        await axios.delete(
          `${BACKEND_URL}/api/payment/deletePayment/${paymentId}`,
          {
            data: {
              karnet_id: karnetId
            },headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        setPayments((prevPayments) =>
          prevPayments.filter((payment) => payment._id !== paymentId)
        );
      } catch (err) {
        console.error(`Failed to delete payment: ${err.message}`);
        setError(`Nie udało się usunąć płatności: ${err.message}`);
      }
    }
  };

  const filteredPayments = payments.filter((payment) => {
    if (!payment.uzytkownik_id) return false;
    const user = users[payment.uzytkownik_id._id] || {};
    const userFullName = `${user.imie || ""} ${
      user.nazwisko || ""
    }`.toLowerCase();
    return userFullName.includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  const indexOfLastPayment = currentPage * itemsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - itemsPerPage;
  const currentPayments = filteredPayments.slice(
    indexOfFirstPayment,
    indexOfLastPayment
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg font-medium text-gray-600">
          Ładowanie płatności...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg font-medium text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto bg-white rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">
        Wszystkie Płatności
      </h1>
      <input
        type="text"
        placeholder="Wyszukaj po imieniu lub nazwisku"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="border border-gray-300 rounded-lg p-3 mb-6 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-gray-300 px-6 py-3 text-left text-sm font-semibold text-gray-700">
                Użytkownik
              </th>

              <th className="border-b border-gray-300 px-6 py-3 text-left text-sm font-semibold text-gray-700">
                Kwota
              </th>
              <th className="border-b border-gray-300 px-6 py-3 text-left text-sm font-semibold text-gray-700">
                Rodzaj Płatności
              </th>
              <th className="border-b border-gray-300 px-6 py-3 text-left text-sm font-semibold text-gray-700">
                Data Płatności
              </th>
              <th className="border-b border-gray-300 px-6 py-3 text-left text-sm font-semibold text-gray-700">
                Notatka
              </th>
              <th className="border-b border-gray-300 px-6 py-3 text-left text-sm font-semibold text-gray-700">
                Akcja
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentPayments.map((payment) => {
              return (
                <tr key={payment._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {payment.uzytkownik_id.imie || "Brak"}{" "}
                    {payment.uzytkownik_id.nazwisko || "Brak"}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    {payment.kwota} PLN
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {payment.karnet_id?.platnosc_rodzaj === "rata"
                      ? "Rata"
                      : "Jednorazowa"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {new Date(payment.data_platnosci).toLocaleDateString(
                      "pl-PL"
                    )}
                  </td>
                  <td className="px-6 py-4">{payment.notatka || "Brak"}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => deletePayment(payment._id, payment.karnet_id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Usuń
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-6">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 rounded-lg bg-blue-500 text-white disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
        >
          Poprzednia
        </button>
        <span className="text-gray-600">
          Strona {currentPage} z {totalPages}
        </span>
        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          className="px-4 py-2 rounded-lg bg-blue-500 text-white disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
        >
          Następna
        </button>
      </div>
    </div>
  );
};

export default AllPayments;
